import { GeometryDataItem } from '@/services/forma/forma-helpers';
import { SpaceData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';

type RenameMap = Record<string, string>;

type ApplyRenameMapArgs = {
	renameMap: RenameMap;
	spaceKeys: Set<string>;
	renameSpaceKey: (fromKey: string, toKey: string) => void;
	renameFunctionKeys: (renameMap: RenameMap, sections?: number[]) => void;
	renamePaletteAssignmentKeys: (renameMap: RenameMap) => void;
};

const extractStableFunctionId = (fullKey: string, buildingPrefix: string) => {
	const keyPart = fullKey.startsWith(buildingPrefix)
		? fullKey.slice(buildingPrefix.length)
		: fullKey;
	const firstDash = keyPart.indexOf('-');
	return firstDash >= 0 ? keyPart.slice(0, firstDash) : keyPart;
};

export const buildFunctionRenameMap = (
	building: string,
	formaData: GeometryDataItem[],
	sourceKeys: string[]
): RenameMap => {
	const buildingPrefix = `${building}-`;
	const targetKeys = formaData.flatMap((item) =>
		item.functions.map((fn) => `${building}-${fn.functionId}`)
	);
	const targetSet = new Set(targetKeys);
	const targetByStableId = new Map<string, string>();

	targetKeys.forEach((key) => {
		targetByStableId.set(extractStableFunctionId(key, buildingPrefix), key);
	});

	const renameMap: RenameMap = {};
	const usedTargets = new Set<string>();
	const filteredSourceKeys = sourceKeys.filter((key) => key.startsWith(buildingPrefix));

	filteredSourceKeys.forEach((sourceKey) => {
		const stableId = extractStableFunctionId(sourceKey, buildingPrefix);
		const targetKey = targetByStableId.get(stableId);
		if (!targetKey || sourceKey === targetKey || usedTargets.has(targetKey)) return;
		renameMap[sourceKey] = targetKey;
		usedTargets.add(targetKey);
	});

	// Fallback when stable id matching fails, but exactly one old/new key differs.
	const unmatchedOld = filteredSourceKeys.filter(
		(sourceKey) => !targetSet.has(sourceKey) && !renameMap[sourceKey]
	);
	const unmatchedNew = targetKeys.filter((targetKey) => {
		if (filteredSourceKeys.includes(targetKey)) return false;
		return !Object.values(renameMap).includes(targetKey);
	});

	if (unmatchedOld.length === 1 && unmatchedNew.length === 1) {
		renameMap[unmatchedOld[0]] = unmatchedNew[0];
	}

	return renameMap;
};

export const applyFunctionRenameMap = ({
	renameMap,
	spaceKeys,
	renameSpaceKey,
	renameFunctionKeys,
	renamePaletteAssignmentKeys,
}: ApplyRenameMapArgs) => {
	if (!renameMap || Object.keys(renameMap).length === 0) return;

	Object.entries(renameMap).forEach(([fromKey, toKey]) => {
		if (spaceKeys.has(fromKey)) {
			renameSpaceKey(fromKey, toKey);
		}
	});

	renameFunctionKeys(renameMap, [2, 4]);
	renamePaletteAssignmentKeys(renameMap);
};

const canonicalFunctionKeyPart = (keyPart: string) => keyPart.replace(/-[^-]+$/, '');

export const resolveLegacySection2Key = (
	targetKey: string,
	building: string,
	section2Data: Record<string, SpaceData>
) => {
	if (section2Data[targetKey]) {
		return targetKey;
	}

	const buildingPrefix = `${building}-`;
	const targetPart = targetKey.slice(buildingPrefix.length);
	const targetCanonical = canonicalFunctionKeyPart(targetPart);

	return Object.keys(section2Data).find((candidate) => {
		if (!candidate.startsWith(buildingPrefix)) return false;
		const candidatePart = candidate.slice(buildingPrefix.length);
		return canonicalFunctionKeyPart(candidatePart) === targetCanonical;
	});
};

export const pickPreferredSpaceData = (...candidates: (SpaceData | undefined)[]) => {
	return candidates.reduce<SpaceData | undefined>((best, candidate) => {
		if (!candidate) return best;
		if (!best) return candidate;
		const bestRooms = best.rooms?.length || 0;
		const candidateRooms = candidate.rooms?.length || 0;
		return candidateRooms > bestRooms ? candidate : best;
	}, undefined);
};
